import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiLock, FiEye, FiServer, FiCloud, FiKey, FiMonitor, FiAlertTriangle } = FiIcons;

const SecurityArchitecture = () => {
  const securityLayers = [
    {
      icon: FiCloud,
      title: "Infrastructure Security",
      description: "Multi-layered security architecture built on enterprise-grade cloud infrastructure",
      features: [
        "AWS/Azure enterprise-grade hosting",
        "Global load balancing with failover",
        "DDoS protection and mitigation",
        "Network segmentation and isolation"
      ]
    },
    {
      icon: FiLock,
      title: "Data Protection",
      description: "End-to-end encryption and secure data handling protocols",
      features: [
        "AES-256 encryption at rest",
        "TLS 1.3 encryption in transit",
        "Zero-trust data access model",
        "Secure backup and recovery"
      ]
    },
    {
      icon: FiKey,
      title: "Access Control",
      description: "Comprehensive identity and access management systems",
      features: [
        "Multi-factor authentication (MFA)",
        "Role-based access control (RBAC)",
        "Single sign-on (SSO) integration",
        "Regular access reviews and audits"
      ]
    },
    {
      icon: FiMonitor,
      title: "Monitoring & Detection",
      description: "24/7 security monitoring and threat detection capabilities",
      features: [
        "Real-time security monitoring",
        "Automated threat detection",
        "Security incident response",
        "Continuous vulnerability scanning"
      ]
    }
  ];

  const complianceStandards = [
    {
      name: "SOC 2 Type II",
      description: "Security, availability, and confidentiality controls"
    },
    {
      name: "ISO 27001",
      description: "Information security management system"
    },
    {
      name: "GDPR",
      description: "European data protection regulation compliance"
    },
    {
      name: "CCPA",
      description: "California consumer privacy act compliance"
    },
    {
      name: "HIPAA",
      description: "Healthcare information privacy and security"
    },
    {
      name: "PCI DSS",
      description: "Payment card industry data security standard"
    }
  ];

  return (
    <>
      <SEOHead
        title="Security Architecture - Namvoro Enterprise Cybersecurity"
        description="Discover Namvoro's enterprise-grade security architecture with military-grade encryption, 24/7 monitoring, and comprehensive threat protection."
        keywords="enterprise security architecture, cybersecurity, military-grade encryption, threat protection, security monitoring, compliance"
        canonicalUrl="https://namvoro.com/security-architecture"
      />
      
      <div className="min-h-screen bg-white pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Security Architecture
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Enterprise-grade security designed to protect your most valuable digital assets with military-grade encryption and comprehensive threat protection.
            </p>
          </motion.div>

          {/* Security Overview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-2xl p-8 mb-16"
          >
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiShield} className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">99.9%</h3>
                <p className="text-slate-600">Security Uptime</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiEye} className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">24/7</h3>
                <p className="text-slate-600">Threat Monitoring</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiLock} className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">256-bit</h3>
                <p className="text-slate-600">AES Encryption</p>
              </div>
            </div>
          </motion.div>

          {/* Security Layers */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Multi-Layer Security Model</h2>
              <p className="text-xl text-slate-600">
                Defense in depth with multiple security layers protecting your data
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {securityLayers.map((layer, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mr-4">
                      <SafeIcon icon={layer.icon} className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{layer.title}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">{layer.description}</p>
                  <ul className="space-y-2">
                    {layer.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-slate-700">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Compliance Standards */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Compliance & Certifications</h2>
              <p className="text-xl text-slate-600">
                Meeting the highest industry standards for security and privacy
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {complianceStandards.map((standard, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  className="bg-slate-50 rounded-lg p-6 text-center"
                >
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{standard.name}</h3>
                  <p className="text-slate-600 text-sm">{standard.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Security Measures */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="bg-slate-900 text-white rounded-2xl p-8"
          >
            <div className="text-center mb-8">
              <SafeIcon icon={FiAlertTriangle} className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Incident Response & Recovery</h2>
              <p className="text-slate-300">
                Comprehensive incident response procedures to minimize impact and ensure rapid recovery
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4">Response Procedures</h3>
                <ul className="space-y-2 text-slate-300">
                  <li>• Immediate threat isolation and containment</li>
                  <li>• Forensic analysis and impact assessment</li>
                  <li>• Stakeholder notification and communication</li>
                  <li>• Recovery and restoration procedures</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Recovery Capabilities</h3>
                <ul className="space-y-2 text-slate-300">
                  <li>• Automated backup and restoration</li>
                  <li>• Disaster recovery within 4 hours</li>
                  <li>• Business continuity planning</li>
                  <li>• Post-incident review and improvement</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-16"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Security Questions?</h2>
            <p className="text-slate-600 mb-6">
              Our security team is available to discuss your specific requirements and compliance needs.
            </p>
            <div className="bg-slate-50 p-6 rounded-lg inline-block">
              <p className="text-slate-700">
                <strong>Security Team:</strong> security@namvoro.com<br />
                <strong>Emergency:</strong> +1 855-425-5551 (24/7)
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default SecurityArchitecture;